<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import { onMounted, ref } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  name: props.data?.name  || props.listCreateName || "",
    price_lists_id: props.data?.price_lists_id || "",
    credit_terms_id: props.data?.credit_terms_id || "",
    payment_types_id: props.data?.payment_types_id || "",
    credit_limit: props.data?.credit_limit || 0,
    accounts_receivable_id: props.data?.accounts_receivable_id || "",
    send_invoice_via: props.data?.send_invoice_via || "",
    auto_reminder: props.data?.auto_reminder || false,
    first_reminder: props.data?.first_reminder || 0,
    second_reminder: props.data?.second_reminder || 0,
    third_reminder: props.data?.third_reminder || 0,
    active: props.data?.active || true,
    uid: props.data?.uid || "",
  });

setFieldRules("name", [required("Name is required")]);
setFieldRules("credit_terms_id", [required("Credit Term is required")]);
setFieldRules("credit_limit", [required("Credit Limit is required")]);
setFieldRules("accounts_receivable_id", [
  required("Receivable Account is required"),
]);
setFieldRules("send_invoice_via", [
  required("Select how the Invoice should be sent"),
]);


const handleSubmit = async (close: boolean, newRecord: boolean) => {
  console.log(newRecord)
  if (validateForm()) {
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        console.log('reset')
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


const sendInvoiceViaList = [
  { id: "Print", name: "Print" },
  { id: "Email", name: "Email" },
];

</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
        <div class="grid gap-6 mb-3">
          <TextInput v-model="fields.name.value" label="Name" placeholder="Enter Name" :readonly="isReadOnly"
            :error="fields.name.touched ? fields.name.error : null" @blur="fields.name.touched = true"></TextInput>

        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <SearchSelect v-model="fields.price_lists_id.value" label="Price List"
            end-point="finance/master-data/price-lists" :readonly="isReadOnly" :error="fields.price_lists_id.touched ? fields.price_lists_id.error : null
              " @blur="fields.price_lists_id.touched = true"  :allowNewRecord="true"/>

          <SearchSelect v-model="fields.credit_terms_id.value" label="Credit Term"
            end-point="finance/master-data/credit-terms" :readonly="isReadOnly" :error="fields.credit_terms_id.touched
              ? fields.credit_terms_id.error
              : null
              " @blur="fields.credit_terms_id.touched = true" :allowNewRecord="true" />

          <SearchSelect v-model="fields.payment_types_id.value" label="Payment Type"
            end-point="finance/master-data/payment-types" :readonly="isReadOnly" :error="fields.payment_types_id.touched
              ? fields.payment_types_id.error
              : null
              " @blur="fields.payment_types_id.touched = true" :allowNewRecord="true" />

          <NumberInput v-model="fields.credit_limit.value" label="Credit Limit" :readonly="isReadOnly" :min="0" :error="fields.credit_limit.touched ? fields.credit_limit.error : null
            " @blur="fields.credit_limit.touched = true" />

          <SearchSelect v-model="fields.accounts_receivable_id.value" label="Receivable Account"
            end-point="finance/accounting/chart-of-accounts" extra="Receivables" :readonly="isReadOnly" :error="fields.accounts_receivable_id.touched
              ? fields.accounts_receivable_id.error
              : null
              " @blur="fields.accounts_receivable_id.touched = true" />

          <SearchSelect v-model="fields.send_invoice_via.value" label="Send Invoice by" :options="sendInvoiceViaList"
            :readonly="isReadOnly" :error="fields.send_invoice_via.touched
              ? fields.send_invoice_via.error
              : null
              " @blur="fields.send_invoice_via.touched = true" />

        </div>
        <div class="grid md:grid-cols-4 gap-6 mt-3">
          <CheckInput v-model="fields.auto_reminder.value" :readonly="isReadOnly" label="Auto Reminder" :error="fields.auto_reminder.touched ? fields.auto_reminder.error : null
            " @blur="fields.auto_reminder.touched = true" />

          <NumberInput v-model="fields.first_reminder.value" label="Third Reminder (days)" :showControls="false"
            :readonly="isReadOnly && fields.auto_reminder.value == true" :min="0" :error="fields.first_reminder.touched ? fields.first_reminder.error : null
              " @blur="fields.first_reminder.touched = true" />

          <NumberInput v-model="fields.second_reminder.value" label="Second Reminder (days)" :showControls="false"
            :readonly="isReadOnly && fields.auto_reminder.value == true" :min="0" :error="fields.second_reminder.touched
              ? fields.second_reminder.error
              : null
              " @blur="fields.second_reminder.touched = true" />

          <NumberInput v-model="fields.third_reminder.value" label="Third Reminder (days)" :showControls="false"
            :readonly="isReadOnly && fields.auto_reminder.value == true" :min="0" :error="fields.third_reminder.touched ? fields.third_reminder.error : null
              " @blur="fields.third_reminder.touched = true" />

          <CheckInput v-model="fields.active.value" :readonly="isReadOnly" label="Active"
            :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />
        </div>
    </template>
  </ModalFormTemplate>
</template>